import {
  Body,
  Icon,
  Tooltip,
  UserCompanySelector,
} from '@assembly-js/design-system';
import { CardContent, CardHeader, FormControl } from '@mui/material';
import classNames from 'classnames';
import { FormikHelpers } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import {
  BANNER_HEIGHT,
  Company,
  DESKTOP_APP_BAR_HEIGHT,
  FormBodyAPI,
  MOBILE_APP_BAR_HEIGHT,
  SCROLLBAR_SIZE,
  User,
} from 'src/constants';
import { RouteContext } from 'src/context';
import { ContractBuilderContext } from 'src/context/contractBuilderContext';
import { RecipientSelector } from 'src/features/recipient-selector/RecipientSelector';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import Activator from 'src/legacy/components/Activator';
import { RegularCardBox } from 'src/legacy/components/Cards/RegularCardBox';
import {
  ContractDetailsPageQueryParams,
  ContractTemplateForm,
} from 'src/legacy/components/Contracts';
import {
  CONTRACT_SIDEBAR_WIDTH,
  ContractBuilderSidebar,
} from 'src/legacy/components/Contracts/ContractBuilderSidebar';
import { getSelectedRecipientAutoFieldValue } from 'src/legacy/components/Contracts/ContractBuilderSidebar/SelectedRecipientInfoInputs';
import { ContractFileDropzone } from 'src/legacy/components/Contracts/ContractFileDropzone';
import { isContractField } from 'src/legacy/components/Contracts/PendingContract';
import { SpinnerContainer } from 'src/legacy/components/Loading';
import RowDivider from 'src/legacy/components/RowDivider';
import { BoxDragPreview } from 'src/legacy/components/Signature/BoxDragPreview';
import {
  SignaturePageContent,
  signaturePageContentWidth,
} from 'src/legacy/components/Signature/SignaturePageContent';
import { useSignatureRequestComponent } from 'src/legacy/components/Signature/SignatureSidebar/useSignatureRequestComponent';
import MemoBaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';
import { TABS_CONTAINER_HEIGHT } from 'src/legacy/components/UI/BaseTab';
import { InputType } from 'src/services/api';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import { RootState } from 'src/store';
import {
  CustomFieldEntityType,
  selectAllCompaniesMap,
  selectClientsMap,
} from 'src/store/clients/types';
import { setPageComponents } from 'src/store/signaturePage/actions';
import { NonHoverBorder } from 'src/theme/colors';
import { hasValidClientContractComponents } from 'src/utils/ContractUtils';

interface StyleProps {
  contentAreaHeight: number;
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  contentContainer: {
    width: '100%',
    // Define the maximum width for displaying the PDF area.
    // Subtract the contract sidebar width and scrollbar thumb width (10px) from the available width
    maxWidth: `calc(100% - ${CONTRACT_SIDEBAR_WIDTH - SCROLLBAR_SIZE}px)`,
    height: props.contentAreaHeight,
    overflowY: 'auto',
  },
  clientContentContainer: {
    [theme.breakpoints.down('sm')]: {
      height: '100vh',
      overflowX: 'hidden',
      overflowY: 'auto',
      paddingTop: MOBILE_APP_BAR_HEIGHT,
    },
  },
  content: {
    width: '100%',
    margin: '0 auto',
    maxWidth: signaturePageContentWidth,
    paddingTop: theme.spacing(5),
  },
  card: {
    boxShadow: 'none',
    borderColor: NonHoverBorder,
    marginBottom: theme.spacing(5),
  },
  cardHeader: {
    borderBottom: `1px solid ${NonHoverBorder}`,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  pdfContainer: {
    position: 'relative',
  },
  flexContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

/**
 * Hook for the contract template creation "FormPage" component.
 *
 * @returns {object} An object containing:
 *   - {object} validationScheme: Form validation rules.
 *   - {JSX.Element} FormRenderer: The form's UI components, including the configuration form, PDF, and draggable signature components.
 */
const noop = () => {};

export const useContractBuilderAndSignerForm = ({
  onPageSave = noop,
}: {
  onPageSave?: () => void;
} = {}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const customFields = useCustomFields(CustomFieldEntityType.CLIENT);
  const isClient = useSelector((state: RootState) => state.user.isClient);
  const { setPlaceholderElement, isOneOffContract, setIsOneOffContract } =
    useContext(ContractBuilderContext);
  const { SignatureComponentsForm } = useSignatureRequestComponent();
  const { query } = useContext(RouteContext);
  const { templateId } = query as ContractDetailsPageQueryParams;
  const [submitDisabledTooltip, setSubmitDisabledTooltip] =
    useState<React.ReactNode>(null);

  // For clients, no validation scheme is applied because no inline errors are shown.
  // Instead, validation will be handled in the save callback.
  const validationScheme = useMemo(
    () =>
      isClient
        ? Yup.object({})
        : Yup.object().shape({
            title: Yup.string().required('Contract must have a name'),
            fileKey: Yup.string(),
            pageComponents: Yup.array().of(Yup.mixed()),
            ...(isOneOffContract
              ? {
                  clientId: Yup.string().required(
                    t(($) => $.validation.required, {
                      field: 'Recipient',
                    }),
                  ),
                  companyId: Yup.string().required(
                    t(($) => $.validation.required, {
                      field: 'Recipient',
                    }),
                  ),
                }
              : {}),
          }),
    [isClient, isOneOffContract, t],
  );

  const FormRenderer = ({
    values,
    handleBlur,
    handleChange,
    touched,
    errors,
    setFieldValue,
  }: FormBodyAPI & FormikHelpers<ContractTemplateForm>) => {
    const isEditFlow = Boolean(templateId);
    const {
      signContractInProgress,
      pageComponents,
      pageLoading,
      pageImages,
      bannerOptions,
      customFieldsMap,
    } = useSelector(
      (state: RootState) => ({
        signContractInProgress: state.signaturePage.saving,
        pageComponents: state.signaturePage.pageComponents,
        pageLoading: state.signaturePage.loading,
        pageImages: state.signaturePage.pageImages,
        bannerOptions: state.ui.bannerOptions,
        customFieldsMap: customFields,
      }),
      shallowEqual,
    );

    const clientsMap = useAppSelector(selectClientsMap);
    const companiesMap = useAppSelector(selectAllCompaniesMap);

    const styleProps = useMemo(() => {
      const contentAreaHeight = isEditFlow
        ? window.innerHeight -
          (DESKTOP_APP_BAR_HEIGHT +
            TABS_CONTAINER_HEIGHT +
            (bannerOptions ? BANNER_HEIGHT : 0))
        : window.innerHeight -
          (DESKTOP_APP_BAR_HEIGHT + (bannerOptions ? BANNER_HEIGHT : 0));

      return {
        contentAreaHeight,
      };
    }, [isEditFlow, bannerOptions]);
    const { classes } = useStyles(styleProps);

    /**
     * For create and edit template flow, display the disabled button tooltip under the following scenarios:
     * 1. The template should have a title.
     * 2. An uploaded file should be associated with the template.
     * 3. At least one client component should be added to the template.
     */
    const hasTitle = Boolean(values.title);
    const hasFileKey = Boolean(values.fileKey);
    const hasValidClientComponent =
      hasValidClientContractComponents(pageComponents);
    const recipientIsMissing =
      isOneOffContract && (!values.clientId || !values.companyId);

    const shouldDisplayTooltip =
      !hasTitle ||
      !hasFileKey ||
      !hasValidClientComponent ||
      recipientIsMissing;

    useEffect(() => {
      if (isClient) return;

      setSubmitDisabledTooltip(
        shouldDisplayTooltip ? (
          <MemoBaseTypography>
            {(() => {
              if (isEditFlow) {
                return t(($) => $.page_contracts.edit_template_disable_tooltip);
              }
              if (isOneOffContract) {
                return t(
                  ($) => $.page_contracts.create_contract_disable_tooltip,
                );
              }
              return t(($) => $.page_contracts.create_template_disable_tooltip);
            })()}
          </MemoBaseTypography>
        ) : null,
      );
    }, [shouldDisplayTooltip, isEditFlow, isOneOffContract]);

    /**
     * Save the page components placed on the PDF in Formik.
     * This is done to seamlessly integrate with existing logic for `isFormChanged`.
     */
    useEffect(() => {
      if (isClient) return;
      setFieldValue('pageComponents', pageComponents);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageComponents]);

    const updatePageComponents = React.useCallback(
      (selectedClient: User, selectedCompany: Company | undefined) => {
        const updatedPageComponents = pageComponents
          .map((component) => {
            const isOneOffContractAutofillField =
              component.inputType === InputType.AutoFill && component.value;

            if (isOneOffContractAutofillField) {
              const updatedValue = getSelectedRecipientAutoFieldValue({
                key: component.autoFillField || '',
                recipient: selectedClient,
                recipientCompany: selectedCompany,
                customFieldsMap,
              });

              if (!updatedValue) return null;

              return {
                ...component,
                value: updatedValue,
              };
            }

            return component;
          })
          .filter(isContractField);

        dispatch(setPageComponents(updatedPageComponents));
      },
      [pageComponents, customFieldsMap, dispatch],
    );

    const handleRecipientSelectorChange = (
      vals: Parameters<
        React.ComponentProps<typeof UserCompanySelector>['onChange']
      >[0],
    ) => {
      const selectedClientCompanyIds = vals.map((val) => ({
        clientId: val.id,
        companyId: val.companyId,
      }));
      if (selectedClientCompanyIds.length > 0) {
        setFieldValue('clientId', selectedClientCompanyIds.at(0)?.clientId);
        setFieldValue('companyId', selectedClientCompanyIds.at(0)?.companyId);
      } else {
        setFieldValue('clientId', '');
        setFieldValue('companyId', '');
      }
      const selectedClient = clientsMap.get(
        selectedClientCompanyIds.at(0)?.clientId || '',
      );
      const selectedCompany = companiesMap.get(
        selectedClientCompanyIds.at(0)?.companyId || '',
      );
      if (selectedClient) {
        updatePageComponents(selectedClient, selectedCompany);
      }
    };

    const handleOneOffContractToggle = (
      _e: React.ChangeEvent<HTMLInputElement>,
      checked: boolean,
    ) => {
      setIsOneOffContract(!checked);
      // reset the page components when the one off contract is toggled
      setFieldValue('clientId', '');
      setFieldValue('companyId', '');
      dispatch(setPageComponents([]));
    };

    return (
      <DndProvider backend={HTML5Backend}>
        <BoxDragPreview />
        <div>
          <div
            className={classNames({
              [classes.contentContainer]: !isClient,
              [classes.clientContentContainer]: isClient,
            })}
          >
            <div
              className={classNames({
                [classes.content]: !isClient,
              })}
            >
              {!isClient && (
                <RegularCardBox className={classes.card}>
                  <CardHeader
                    className={classes.cardHeader}
                    title={
                      <MemoBaseTypography fontType="15Medium">
                        Contract setup
                      </MemoBaseTypography>
                    }
                  />

                  <CardContent className={classes.cardContent}>
                    <ContractFileDropzone
                      name="fileKey"
                      onRemoveUpload={() => {
                        setFieldValue('title', '');
                      }}
                    />
                    <BaseTextField
                      label="Contract name"
                      variant="outlined"
                      sizeVariant="medium"
                      value={values.title}
                      fullWidth
                      name="title"
                      error={Boolean(touched?.title && errors?.title)}
                      helperText={(touched.title && errors.title) || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {/* 1. Only show one off contract toggle if the feature flag is enabled
                        2. Don't show the one off contract toggle during the edit flow. */}
                    {!isEditFlow && (
                      <>
                        <RowDivider mt={0} mb={0} />
                        <div className={classes.flexContainer}>
                          <div className={classes.flexContainer}>
                            <MemoBaseTypography fontType="13Regular">
                              This contract is a reusable template
                            </MemoBaseTypography>
                            <Tooltip
                              position="bottom"
                              content={
                                <Body size="sm" className="max-w-[373px]">
                                  {t(
                                    ($) =>
                                      $.page_contracts
                                        .reusable_template_tooltip,
                                  )}
                                </Body>
                              }
                            >
                              <div>
                                <Icon
                                  icon="Info"
                                  className="h-3 w-3 ml-2 text-secondary"
                                />
                              </div>
                            </Tooltip>
                          </div>
                          <Activator
                            checked={!isOneOffContract}
                            onChange={handleOneOffContractToggle}
                          />
                        </div>
                        {isOneOffContract && (
                          <FormControl fullWidth>
                            <RecipientSelector
                              limitSelectedOptions={1}
                              label="Select a recipient"
                              recipientTypes={['client']}
                              onRecipientSelected={
                                handleRecipientSelectorChange
                              }
                              placeholder={t(($) => $.actions.select_clients)}
                              name="contractRecipientIds"
                            />
                          </FormControl>
                        )}
                      </>
                    )}
                  </CardContent>
                </RegularCardBox>
              )}

              {signContractInProgress && (
                <SpinnerContainer cover message={null} />
              )}
              <div className={classes.pdfContainer}>
                {!pageLoading && pageImages.length > 0 && (
                  <SignaturePageContent
                    onSavePlaceholderElementReference={setPlaceholderElement}
                    onPageSave={onPageSave}
                    isEditFlow={isEditFlow}
                  >
                    <SignatureComponentsForm />
                  </SignaturePageContent>
                )}
              </div>
            </div>
          </div>
          {!isClient && <ContractBuilderSidebar />}
        </div>
      </DndProvider>
    );
  };

  return {
    validationScheme,
    FormRenderer,
    submitDisabledTooltip,
  };
};
